/**
 * Lane C0-OUTFIT2X: garments painted natively at the figure grid. A garment keeps its cut from the existing parts
 * library (clothes.ts, gear.ts, flowParts.ts: collars 右衽, sleeves, skirts that clear a stride, the cloth's sway),
 * drawn in role colours; every pixel is then painted at full resolution: material tones lit across the form, folds
 * fanning down the skirt, embroidered hem bands (回纹 key fret, cloud curls) between gold pipings, cuffs, piping on
 * the trims, a woven motif on the large panels, and shadow under whatever lies over the cloth.
 */
import type { Layer, Piece } from '../figure';
import type { NativeCtx } from './native';
import {
  above,
  below,
  J,
  kindOf,
  makeAcross,
  type Mat,
  motifAt,
  mendStandIns,
  occlusion,
  type Role,
  Roles,
  sentinel,
  tone,
} from './paint';

/** An embroidered band: motif rows between two gold pipings. */
export interface Band {
  motif: readonly string[];
  /** The band's ground and the motif's thread. */
  ground: Mat;
  thread: Mat;
  piping: Mat;
}

/** How a garment is painted. */
export interface Dress {
  /** Material of each role colour the garment is drawn in; a role left out keeps its drawn colour. */
  mats: Partial<Record<Role, Mat>>;
  /** Band round the bottom of a skirt or robe (trunk pieces, below the waist). */
  hem?: Band;
  /** Band round the end of each sleeve. */
  cuff?: { ground: Mat; piping: Mat; rows: number };
  /** Folds fanning down the skirt below the waist (`pleats`: close, regular). */
  folds?: 'folds' | 'pleats';
  /** A woven motif over the cloth (`x` a light thread, `o` a dark one), cell size in canvas pixels. */
  brocade?: { motif: readonly string[]; sx: number; sy: number };
  /** A gold line along the trims where they meet the cloth. */
  piping?: Mat;
  /** Stitches along the middle of a belt-like part (leather). */
  stitch?: boolean;
  /** Below the sash the drawn decoration (trim-, lining- and accent-coloured motifs) is left out: plain cloth. */
  plainSkirt?: boolean;
  /**
   * Keep the design's own lines drawn in the cloth's shades inside the shape (patchwork seams, quilting, pleats, fold
   * lines, a brocade's light threads): relit, but where the design put them. Its edge shading is not kept (that was
   * for the light on one side only).
   */
  keepLines?: boolean;
  /** A costume read pass (opt-in per outfit, others unchanged): see `ReadPass`. */
  read?: ReadPass;
}

/**
 * Costume read pass (ns-costume 10-11, the plain Wudang robe read as a blank pale column): on light plain silk the
 * design's short fold strokes read as rain and a sleeve vanishes into the robe behind it. With this pass the short
 * strokes on the skirt are left out (long lines such as panel seams stay), a fixed few long folds run from the sash
 * to the hem (darker and wider at the hem), and a sleeve shows its lining as a line wherever it lies over the robe.
 */
export interface ReadPass {
  /** The sleeve lining seen at a sleeve's edge where it lies over other cloth. */
  rim: Mat;
  /** Long skirt folds: where each runs across the skirt (0 the left edge … 1 the right, as seen). */
  folds: readonly number[];
  /** The sash's two tails hanging down the skirt (toward the facing side when turned): the sect's wind ribbon. */
  ribbon?: Mat;
  /** The hem: a band (dark edge, piping, motif rows, piping; motif length + 5 rows) across the whole skirt, in place of the design's inset bar. */
  hem?: Band;
}


/** The colour inputs of a garment, by role. */
export type Colours = Partial<Record<Role, string>>;

/**
 * A garment layer from the parts library, painted natively: `make` builds it from the stand-in colours of `real`.
 */
export function nativeLayer(make: (s: Colours) => Layer, real: Colours, dress: Dress): Layer {
  const s: Colours = {};
  const entries: [Role, string][] = [];
  for (const [role, hex] of Object.entries(real) as [Role, string][]) {
    s[role] = sentinel(role, hex);
    entries.push([role, s[role]!]);
  }
  const roles = new Roles(entries);
  const base = make(s);
  return {
    ...base,
    native: true,
    pieces: (sk, fit) =>
      base.pieces(sk, fit).map((p): Piece => ({ ...p, native: (c) => paintCloth(c, roles, dress) })),
  };
}

/** Paints one garment piece. */
export function paintCloth(c: NativeCtx, roles: Roles, d: Dress): void {
  const kind = kindOf(c);
  const across = makeAcross(c, kind);
  const waist = J(c, 'waist');
  const pelvis = J(c, 'pelvis');
  const chest = J(c, 'chest');
  const hemRows = d.hem ? d.hem.motif.length + 4 : 0;
  const forearm = kind === 'armA' || kind === 'armB' ? ([J(c, `elbow${kind.slice(3) as 'A' | 'B'}`), J(c, `wrist${kind.slice(3) as 'A' | 'B'}`)] as const) : undefined;
  const roleAt = (x: number, y: number) => roles.of(c.at(x, y));
  const painted = new Uint8Array(c.bw * c.bh);
  const sashY = d.plainSkirt ? lowestRow(c, roles, 'sash') : Infinity;
  for (let y = c.by; y < c.by + c.bh; y++)
    for (let x = c.bx; x < c.bx + c.bw; x++) {
      if (!c.vis(x, y)) continue;
      const hit = roles.hit(c.at(x, y)) ?? roles.hit(c.src(x, y));
      let role = hit?.role ?? null;
      if (!role) continue;
      if (y > sashY && (role === 'trim' || role === 'accent' || role === 'inner') && below(c, x, y) > 3) role = 'cloth';
      // Read pass: the design's inset hem bar (a horizontal run of trim low on the skirt) becomes cloth; the band replaces it.
      if (d.read?.hem && kind === 'trunk' && role !== 'cloth' && y > waist.y + 3 && below(c, x, y) < 34 && roleAt(x - 2, y) === role && roleAt(x + 2, y) === role)
        role = 'cloth';
      const m = d.mats[role];
      if (!m) continue;
      const a = across(x, y);
      let lv = 0;
      if (a.w >= 5) {
        if (a.t < 0.22) lv = -1;
        else if (a.lit <= 1 && a.w >= 7) lv = 1;
      } else if (a.t < 0.34) lv = -1;
      if (m.kind === 'silk' && a.w >= 8 && a.lit === 2) lv = 2;
      if (m.kind === 'leather' && above(c, x, y) === 0) lv = Math.max(lv, 1);
      const skirt = kind === 'trunk' && y > waist.y + 3;
      // Folds below the waist: shadow lines fanning out toward the hem, each with its lit ridge on the light side.
      if (d.folds && role === 'cloth' && skirt && a.w >= 10) {
        const fromL = c.light > 0 ? a.dark : a.lit;
        const step = d.folds === 'pleats' ? 6 : 9;
        const nf = Math.max(1, Math.round(a.w / step));
        const dy = y - waist.y;
        for (let k = 0; k < nf; k++) {
          if (dy < 6 + (k % 2) * 7) continue;
          const at = Math.round(((k + 0.5) * a.w) / nf + Math.sin(dy / 11 + k * 1.7) * (d.folds === 'pleats' ? 0.4 : 1.1));
          if (fromL === at) lv = Math.min(lv, below(c, x, y) < 10 ? -2 : -1);
          else if (fromL === at + c.light) lv = Math.max(lv, 1);
        }
      }
      // A woven motif on the cloth, laid out from the body so it moves with it.
      if (d.brocade && role === 'cloth') {
        const { motif, sx, sy } = d.brocade;
        const v = y - chest.y;
        const row = Math.floor(v / sy);
        const u = x - pelvis.x + (row % 2 ? Math.floor(sx / 2) : 0);
        const cu = ((u % sx) + sx) % sx;
        const cv = ((v % sy) + sy) % sy;
        const ch = motif[cv]?.[cu];
        if (ch === 'x') lv = Math.min(2, lv + 1);
        else if (ch === 'o') lv = Math.max(-2, lv - 1);
      }
      // Costume read pass: long, fixed folds from the sash to the hem instead of strokes.
      if (d.read && role === 'cloth' && skirt && a.w >= 10) {
        const fromL = c.light > 0 ? a.dark : a.lit;
        const dy = y - waist.y;
        const db = below(c, x, y);
        d.read.folds.forEach((f, k) => {
          if (dy < 5 + k * 4) return;
          const at = Math.round(f * (a.w - 1) + Math.sin(dy / 13 + k * 2.3) * 1.2);
          const dist = fromL - at;
          const low = db < (dy + db) * 0.4;
          if (dist === 0 || dist === -c.light) lv = Math.min(lv, low && dist === 0 ? -2 : -1);
          else if (dist === c.light) lv = Math.max(lv, 1);
        });
      }
      const stroke = d.read && role === 'cloth' && skirt && hit && shortStroke(c, roles, x, y, hit.dl);
      if (d.keepLines && hit && a.dark > 3 && a.lit > 3 && !stroke) {
        if (hit.dl <= -0.09) lv = Math.min(lv, hit.dl <= -0.22 ? -2 : -1);
        else if (hit.dl >= 0.04) lv = Math.max(lv, 1);
      }
      const occ = occlusion(c, x, y);
      if (occ < 0) lv = Math.min(lv, occ);
      let col = tone(m.ramp, lv);
      // Costume read pass: the sleeve's lining where its edge lies over other cloth, the shade one pixel in.
      if (d.read && role === 'cloth' && (kind === 'armA' || kind === 'armB')) {
        const rim = rimAt(c, x, y);
        if (rim === 1) col = tone(d.read.rim.ramp, lv >= 1 ? 0 : -1);
        else if (rim === 2) col = tone(m.ramp, Math.min(lv, -1));
      }
      if (d.read && kind === 'trunk' && y > waist.y + 3) {
        const db = below(c, x, y);
        const dy = y - waist.y;
        // The wind ribbon: two tails from the sash down the front, toward the facing side when turned.
        if (d.read.ribbon && role === 'cloth' && a.w >= 10 && dy >= 2) {
          const fromL = c.light > 0 ? a.dark : a.lit;
          const t = dy / (dy + db);
          const sway = Math.round(Math.sin(dy / 9) * 0.8);
          // Head-on: off the middle (in front the knot sits to one side; behind, the tails hang beside the spine).
          const base = Math.round((c.sk.square ? (c.sk.back ? 0.54 : 0.36) : c.sk.back ? 0.3 : 0.8) * (a.w - 1)) + sway;
          const side = c.sk.square ? 1 : -1;
          for (const [off, wd, len] of [
            [0, 3, 0.58],
            [4 * side, 2, 0.42],
          ] as const) {
            const u = fromL - (base + off);
            if (u < 0 || u >= wd || t > len) continue;
            // Frayed ends: alternate columns stop a row short.
            if (t > len - 0.03 && (u + dy) % 2) continue;
            col = tone(d.read.ribbon.ramp, u === 0 ? -1 : u === wd - 1 && wd > 2 ? 1 : 0);
          }
        }
        if (d.read.hem && db < d.read.hem.motif.length + 5) col = readHem(d.read.hem, db, x - pelvis.x, lv);
      }
      // Gold piping where a trim meets the cloth.
      if (d.piping && role === 'trim') {
        // Only on a trim band wide enough to keep its own colour inside the piping (a thin line stays trim).
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const)
          if (roleAt(x + dx, y + dy) === 'cloth' && roleAt(x - dx, y - dy) === 'trim' && roleAt(x - 2 * dx, y - 2 * dy) === 'trim' && roleAt(x - 3 * dx, y - 3 * dy) === 'trim') {
            col = tone(d.piping.ramp, lv >= 0 ? 1 : 0);
            break;
          }
      }
      // Stitches down the middle of a belt.
      if (d.stitch && role === 'cloth' && a.w >= 4 && Math.abs(a.dark - a.lit) <= 0 && (x + y) % 3 === 0)
        col = tone(m.ramp, 1);
      // The embroidered hem.
      if (d.hem && !d.read?.hem && role === 'cloth' && skirt) {
        const db = below(c, x, y);
        if (db < hemRows) col = bandAt(d.hem, db, x - pelvis.x, lv) ?? col;
      }
      // Cuffs: a band at the end of the sleeve.
      if (d.cuff && forearm && role === 'cloth') {
        const [e, w] = forearm;
        const len = Math.hypot(w.x - e.x, w.y - e.y) || 1;
        const along = ((x - e.x) * (w.x - e.x) + (y - e.y) * (w.y - e.y)) / len;
        const past = along - len;
        if (past > -d.cuff.rows - 1) col = past > -d.cuff.rows ? tone(d.cuff.ground.ramp, lv) : tone(d.cuff.piping.ramp, lv >= 0 ? 1 : 0);
      }
      c.set(x, y, col);
      painted[(y - c.by) * c.bw + (x - c.bx)] = 1;
    }
  mendStandIns(c, roles, painted);
}

/** The read pass's hem band `db` rows above the hem: a dark edge, piping, the motif on the ground, piping, ground. */
function readHem(b: Band, db: number, u: number, lv: number): number {
  const mh = b.motif.length;
  if (db === 0) return tone(b.ground.ramp, -2);
  if (db === 1) return tone(b.ground.ramp, -1);
  if (db === 2 || db === mh + 4) return tone(b.piping.ramp, lv >= 0 ? 1 : 0);
  if (db >= 3 && db < mh + 3) {
    const ch = motifAt(b.motif, u, mh - 1 - (db - 3));
    return ch === '#' ? tone(b.thread.ramp, lv >= 0 ? 1 : 0) : tone(b.ground.ramp, Math.min(lv, 0));
  }
  return tone(b.ground.ramp, Math.min(lv, 0));
}

/** A design line pixel (`dl` its step) that is part of a short upright stroke (under 8 pixels): rain on light silk. */
function shortStroke(c: NativeCtx, roles: Roles, x: number, y: number, dl: number): boolean {
  const dark = dl <= -0.09;
  if (!dark && dl < 0.04) return false;
  const line = (yy: number) => {
    const h = roles.hit(c.at(x, yy));
    return !!h && (dark ? h.dl <= -0.09 : h.dl >= 0.04);
  };
  let n = 1;
  for (let k = 1; k < 8 && line(y - k); k++) n++;
  for (let k = 1; k < 8 && line(y + k); k++) n++;
  return n < 8;
}

/**
 * Where a limb piece's pixel lies against other cloth behind it: 1 on its edge, 2 one pixel in, 0 elsewhere (bare
 * canvas outside takes the figure's outline later, and a nearer piece casts its own shadow).
 */
function rimAt(c: NativeCtx, x: number, y: number): 0 | 1 | 2 {
  let best: 0 | 1 | 2 = 0;
  // Not up: where the sleeve meets the shoulder it is sewn on, not lying over.
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1]] as const)
    for (const k of [1, 2] as const) {
      const nx = x + dx * k;
      const ny = y + dy * k;
      if (c.at(nx, ny) >>> 24) continue;
      if (k === 2 && !(c.at(x + dx, y + dy) >>> 24)) break;
      if (c.get(nx, ny) >>> 24 && !c.nearer(nx, ny)) {
        if (k === 1) return 1;
        best = 2;
      }
      break;
    }
  return best;
}

/** The lowest row where a role shows in the piece's middle columns (Infinity if nowhere). */
function lowestRow(c: NativeCtx, roles: Roles, role: Role): number {
  let low = -Infinity;
  for (let y = c.by; y < c.by + c.bh; y++)
    for (let x = c.bx; x < c.bx + c.bw; x++) if (roles.of(c.at(x, y)) === role) low = Math.max(low, y);
  return low === -Infinity ? Infinity : low;
}

/** A hem band's colour `db` pixels above the hem (undefined above the band). */
function bandAt(b: Band, db: number, u: number, lv: number): number | undefined {
  const mh = b.motif.length;
  if (db === 0) return tone(b.ground.ramp, -2);
  if (db === 1 || db === mh + 2) return tone(b.piping.ramp, lv >= 0 ? 1 : 0);
  if (db === mh + 3) return tone(b.ground.ramp, -1);
  if (db >= 2 && db < mh + 2) {
    const ch = motifAt(b.motif, u, mh - 1 - (db - 2));
    return ch === '#' ? tone(b.thread.ramp, lv >= 0 ? 1 : 0) : tone(b.ground.ramp, lv);
  }
  return undefined;
}
